import { HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { JwtService } from '../services/jwt.service';
import { storageKey } from 'src/app/shared/utils/storage.util';


@Injectable()
export class InterceptorService implements HttpInterceptor {
  constructor(
    private readonly notificationService: NotificationService,
    private readonly jwtService: JwtService
  ) {}
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Capture the token used for this request to detect stale 401s
    const tokenUsedForRequest = req.headers.get('Authorization')?.replace('Bearer ', '') ?? null;

    return next.handle(req).pipe(
      filter((event) => event instanceof HttpResponse),
      tap((e) => {
        if (e.type === HttpEventType.Response && e.headers.get('version')) {
          const cachedVersion = localStorage.getItem(storageKey('app_version'));
          const apiVersion = e.headers.get('version');
          if (!cachedVersion) {
            localStorage.setItem(storageKey('app_version'), apiVersion);
            this.notificationService.showReloadButton$.next(true);
            return;
          }
          try {
            const cachedParts = cachedVersion.split('.').map(Number);
            const apiParts = apiVersion.split('.').map(Number);

            for (let i = 0; i < cachedParts.length; i++) {
              if (cachedParts[i] > apiParts[i]) break;
              if (cachedParts[i] < apiParts[i]) {
                localStorage.setItem(storageKey('app_version'), apiVersion);
                this.notificationService.showReloadButton$.next(true);
              }
            }
          } catch (error) {
            this.notificationService.error('Versioning error, contact support!', error);
          }
        }
      }),
      catchError((error) => {
        // If a 401 occurs but the token has been refreshed since this request was sent,
        // retry with the new token instead of logging out. This handles race conditions
        // where concurrent requests use a token that gets revoked by a parallel request.
        if (
          error.status === 401 &&
          error.error?.errorCode === 'DEFAULT_TOKEN_EXPIRED' &&
          tokenUsedForRequest &&
          this.jwtService.token &&
          this.jwtService.token !== tokenUsedForRequest
        ) {
          const retryReq = req.clone({
            setHeaders: { 'Authorization': `Bearer ${this.jwtService.token}` }
          });
          return next.handle(retryReq);
        }

        return throwError(() => error);
      })
    );
  }
}
